import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { formatNumberWithClosestFactor } from "@entrilia/shared/utilities/formatters";
import type { WorkflowTokenUsage } from "./workflowTokenUsageHelper";

interface Props {
  open: boolean;
  onClose: () => void;
  tokenUsage: WorkflowTokenUsage;
}

const cellSx = { py: 0.5, px: 1, border: "none", lineHeight: "36px" } as const;
const numericCellSx = { ...cellSx, textAlign: "right", fontVariantNumeric: "tabular-nums" } as const;

const formatTokens = (value?: number): string =>
  value !== undefined ? formatNumberWithClosestFactor(value, { maximumFractionDigits: 1 }) : "-";

const WorkflowTokenUsageDialog = ({ open, onClose, tokenUsage }: Props) => {
  const { groups, totals } = tokenUsage;

  if (groups.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>AI Token Usage</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <Divider />
      <DialogContent>
        <Table size="small" sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TableCell sx={{ ...cellSx, width: "auto" }}>
                <Typography variant="caption" color="textSecondary">
                  Activity
                </Typography>
              </TableCell>
              <TableCell sx={{ ...cellSx, width: 120 }}>
                <Typography variant="caption" color="textSecondary">
                  Agent
                </Typography>
              </TableCell>
              <TableCell sx={{ ...cellSx, width: 120, textAlign: "right" }}>
                <Typography variant="caption" color="textSecondary">
                  Total Input
                </Typography>
              </TableCell>
              <TableCell sx={{ ...cellSx, width: 120, textAlign: "right" }}>
                <Typography variant="caption" color="textSecondary">
                  Cached Input
                </Typography>
              </TableCell>
              <TableCell sx={{ ...cellSx, width: 120, textAlign: "right" }}>
                <Typography variant="caption" color="textSecondary">
                  Output
                </Typography>
              </TableCell>
              <TableCell sx={{ ...cellSx, width: 140 }}>
                <Typography variant="caption" color="textSecondary">
                  Model
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((group, groupIndex) => (
              <TableRow
                // eslint-disable-next-line @eslint-react/no-array-index-key
                key={`${group.activityName}-${groupIndex}`}
                sx={{ backgroundColor: groupIndex % 2 === 0 ? "transparent" : "action.hover" }}
              >
                <TableCell sx={{ ...cellSx, verticalAlign: "middle" }}>
                  <Typography>{group.activityName}</Typography>
                </TableCell>
                <TableCell sx={{ ...cellSx, verticalAlign: "top" }}>
                  <Stack>
                    {group.rows.map((row) => (
                      <Typography key={row.agentType} variant="body1">
                        {row.agentType}
                      </Typography>
                    ))}
                  </Stack>
                </TableCell>
                <TableCell sx={{ ...numericCellSx, verticalAlign: "top" }}>
                  <Stack>
                    {group.rows.map((row) => (
                      <Typography key={row.agentType} variant="body1">
                        {formatTokens(row.inputTokens)}
                      </Typography>
                    ))}
                  </Stack>
                </TableCell>
                <TableCell sx={{ ...numericCellSx, verticalAlign: "top" }}>
                  <Stack>
                    {group.rows.map((row) => (
                      <Typography key={row.agentType} variant="body1">
                        {formatTokens(row.cachedInputTokens)}
                      </Typography>
                    ))}
                  </Stack>
                </TableCell>
                <TableCell sx={{ ...numericCellSx, verticalAlign: "top" }}>
                  <Stack>
                    {group.rows.map((row) => (
                      <Typography key={row.agentType} variant="body1">
                        {formatTokens(row.outputTokens)}
                      </Typography>
                    ))}
                  </Stack>
                </TableCell>
                <TableCell sx={{ ...cellSx, verticalAlign: "top" }}>
                  <Stack>
                    {group.rows.map((row) => (
                      <Typography key={row.agentType} variant="body1">
                        {row.modelName && row.reasoningEffort
                          ? `${row.modelName} · ${row.reasoningEffort}`
                          : (row.modelName ?? "-")}
                      </Typography>
                    ))}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {totals.length > 0 &&
              totals.map((total, index) => (
                <TableRow key={total.modelName} sx={{ borderTop: index === 0 ? 2 : 0, borderColor: "divider" }}>
                  <TableCell sx={cellSx}>
                    <Typography variant="subtitle2">{`Total (${total.modelName})`}</Typography>
                  </TableCell>
                  <TableCell sx={cellSx} />
                  <TableCell sx={numericCellSx}>
                    <Typography variant="subtitle2">{formatTokens(total.inputTokens)}</Typography>
                  </TableCell>
                  <TableCell sx={numericCellSx}>
                    <Typography variant="subtitle2">{formatTokens(total.cachedInputTokens)}</Typography>
                  </TableCell>
                  <TableCell sx={numericCellSx}>
                    <Typography variant="subtitle2">{formatTokens(total.outputTokens)}</Typography>
                  </TableCell>
                  <TableCell sx={cellSx} />
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogButtons>
        <Button variant="contained" color="primary" onClick={onClose}>
          Close
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default WorkflowTokenUsageDialog;
